import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getConfigs, setAuth0Methods } from '../../api';
import EnumTable from '../../components/common/EnumTable';

const HomePage = () => {
  const [configs, setConfigs] = useState([]);
  const { user, logout, getAccessTokenSilently } = useAuth0();

  // Set auth methods for API calls
  useEffect(() => {
    setAuth0Methods({
      getAccessTokenSilently,
      loginWithRedirect: () => window.location.href = '/login'
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const data = await getConfigs();
        setConfigs(data);
      } catch (error) {
        console.error("Error fetching configurations:", error);
      }
    };
    fetchConfigs();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with user info and logout */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-purple-600">Get Talky Command Center</h1>
              <div className="text-sm text-gray-500">
                Environment: <span className="font-semibold">Production</span> | 
                Version: <span className="font-semibold">1.0.0</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                <div className="text-sm text-gray-500">{user?.email}</div>
              </div>
              <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-purple-600">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-md hover:bg-gray-100 transition duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="p-6">
        <div id="app-environment" style={{ display: 'none' }}></div>
        <div id="notification" className="hidden"></div>
        <div className="container mx-auto max-w-7xl p-4">
          <div id="global-config" className="tab-content">
            <div className="banner mb-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg">
              <h2 className="text-xl font-semibold">Global Configuration</h2>
              <p className="mt-2 opacity-90">Manage your Get Talky configurations and settings.</p>
            </div>
            
            <div className="space-y-6 p-6">
              <div className="card bg-white rounded-lg shadow-md">
                <div className="card-header bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Client Configurations</h3>
                </div>
                <div className="p-6">
                  <div id="config-list" className="mb-4 table-container">
                    {configs.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client ID</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Internal ID</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {configs.map((config) => (
                              <tr key={config.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.name || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.client_id || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.client_internal_id || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <Link 
                                    to={`/edit/${config.id}`} 
                                    className="text-purple-600 hover:text-purple-900 font-medium hover:underline"
                                  >
                                    Edit
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-gray-500 mb-4">No configurations found.</div>
                        <p className="text-sm text-gray-400">Get started by adding your first configuration.</p>
                      </div>
                    )}
                  </div>
                  <Link 
                    to="/add" 
                    className="inline-block w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg text-center transition duration-200 transform hover:scale-105"
                  >
                    Add New Configuration
                  </Link>
                </div>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <h2 className="text-2xl font-semibold text-purple-600 mb-4">Enum Tables</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <EnumTable enumType="client-type" title="Client Types" />
                <EnumTable enumType="user-role" title="User Roles" />
                <EnumTable enumType="provider-type" title="Provider Types" />
                <EnumTable enumType="interactor-role" title="Interactor Roles" />
                <EnumTable enumType="request-content-type" title="Request Content Types" />
                <EnumTable enumType="request-status" title="Request Statuses" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 